import React from 'react';

import { Capacitor } from '@capacitor/core';
import { defineCustomElements } from '@ionic/pwa-elements/loader';
import * as Sentry from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';
import ReactDOM from 'react-dom/client';

import {
  isChunkLoadError,
  recoverFromChunkLoadError,
  registerChunkLoadRecovery,
} from '@liscio/common';

import App from './App';
import { config } from './config';
import reportWebVitals from './reportWebVitals';
import { beforeBreadcrumb } from './sentryBreadcrumbs';
import { UnexpectedError } from 'modules/errors/views';

import './index.css';

if (Capacitor.getPlatform() === 'android') {
  document.documentElement.classList.add('native-android');
}

// CI touchpoint: keep firm path included for integration workflow validation.
// TODO: DEV-3583 Hoist Sentry wrapper to @liscio/common
if (config.sentry.isEnabled) {
  Sentry.init(
    {
      dsn: config.sentry.dsn,
      release: config.version,
      environment: config.appIdentifier,
      beforeBreadcrumb,
    },
    SentryReact.init
  );
}

const chunkLoadRecoveryOptions = { appVersion: config.version };

registerChunkLoadRecovery(chunkLoadRecoveryOptions);

const handleErrorBoundaryError = (error: unknown) => {
  recoverFromChunkLoadError(error, chunkLoadRecoveryOptions);
};

const tagChunkLoadRecoveryError = (
  scope: { setTag: (key: string, value: string) => void },
  error: unknown
) => {
  if (isChunkLoadError(error)) {
    scope.setTag('chunk_load_recovery', 'attempted');
  }
};

const AppComponent = config.sentry.isEnabled ? (
  <SentryReact.ErrorBoundary
    beforeCapture={tagChunkLoadRecoveryError}
    fallback={<UnexpectedError />}
    onError={handleErrorBoundaryError}
  >
    <App />
  </SentryReact.ErrorBoundary>
) : (
  <App />
);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(<React.StrictMode>{AppComponent}</React.StrictMode>);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Call the element loader after the app has been rendered the first time
defineCustomElements(window);
